import { ReactElement, useMemo, useCallback, useRef } from 'react';
import { Typography } from '@mui/material';
import Map, {
  NavigationControl,
  FullscreenControl,
  Marker,
  Popup,
} from 'react-map-gl';
import mapboxgl from 'mapbox-gl';
import { Feature, Polygon } from 'geojson';
import { useFormikContext } from 'formik';

import { TOKEN } from '~utils/mapbox';
import { controlLocale } from '~constants/mapControlLocale';
import { MarkerWarehouse, StopsPopupMap } from '~components/index';
import {
  ModeSateliteControl,
  DrawPolygonControl,
} from '~components/MapCustomControl';
import { DrawPolygonControlRef } from '~components/MapCustomControl/DrawPolygonControl/types';
import { StopsPopupMapProps } from '~components/StopsPopupMap/types';
import { MapMarkerDragHintContainer } from '~components/MapMarkerDragHint/styles';
import { usePopupData } from '~hooks/index';
import { MapPopupData, Nullable } from '~globals/types/commons';
import { WarehouseExtended } from '~services/warehouse/types';

import { ConfigLogisticZoneFormMapProps } from './types';
import { ConfigLogisticZoneFormMapContainer } from './styles';
import { getInitialValues, FIELDS_NAME } from './utils';

const ConfigLogisticZoneFormMap = ({
  warehouses,
}: ConfigLogisticZoneFormMapProps): ReactElement => {
  const { values, setFieldValue, setFieldTouched } =
    useFormikContext<ReturnType<typeof getInitialValues>>();

  const drawPolygonControlRef = useRef<DrawPolygonControlRef>(null);

  const colorDraw = useMemo(() => values[FIELDS_NAME.COLOR], [values]);
  const defaultPolygonFeature = useMemo(
    () => values[FIELDS_NAME.POLYGON_FEATURE],
    [values],
  );

  const bounds = useMemo(() => {
    const boundsInstance = new mapboxgl.LngLatBounds();

    warehouses.forEach((warehouse) => {
      const warehouseCoords = new mapboxgl.LngLat(
        warehouse.longitude,
        warehouse.latitude,
      );

      boundsInstance.extend(warehouseCoords);
    });

    defaultPolygonFeature?.geometry.coordinates[0].forEach((coord) => {
      const polygonCoords = new mapboxgl.LngLat(coord[0], coord[1]);

      boundsInstance.extend(polygonCoords);
    });

    return boundsInstance;
  }, [warehouses, defaultPolygonFeature]);

  const handleChangePolygon = useCallback(
    (features: Nullable<Feature<Polygon>[]>) => {
      const currentFeature = features?.[0] ?? null;

      setFieldTouched(FIELDS_NAME.POLYGON_FEATURE, true);
      setFieldValue(FIELDS_NAME.POLYGON_FEATURE, currentFeature);
    },
    [setFieldTouched, setFieldValue],
  );

  const {
    data: dataPopupMarker,
    handleOpenPopup: handleOpenPopupMarker,
    handleClosePopup: handleClosePopupMarker,
  } = usePopupData<Nullable<MapPopupData<StopsPopupMapProps>>>(null);

  const handleClickWarehouseMarker = useCallback(
    (
      event: mapboxgl.MapboxEvent<MouseEvent>,
      currentWarehouse: WarehouseExtended,
    ) => {
      if (drawPolygonControlRef.current?.isDrawing) return null;

      const popupData: MapPopupData<StopsPopupMapProps> = {
        coordinates: {
          longitude: currentWarehouse.longitude,
          latitude: currentWarehouse.latitude,
        },
        data: {
          title: currentWarehouse.title,
          address: currentWarehouse.address ?? '',
          timeWindow: {
            allDay: currentWarehouse.timeWindowAllDay,
            windowFrom1: currentWarehouse.timeWindowFrom1,
            windowFrom2: currentWarehouse.timeWindowFrom2,
            windowTo1: currentWarehouse.timeWindowTo1,
            windowTo2: currentWarehouse.timeWindowTo2,
          },
        },
      };

      handleOpenPopupMarker(event, popupData);
    },
    [handleOpenPopupMarker],
  );

  return (
    <ConfigLogisticZoneFormMapContainer>
      <Map
        mapboxAccessToken={TOKEN}
        mapStyle="mapbox://styles/mapbox/light-v10"
        initialViewState={{
          bounds,
          fitBoundsOptions: {
            padding: 160,
            maxZoom: 12,
          },
        }}
        attributionControl={false}
        locale={controlLocale}
      >
        <FullscreenControl position="bottom-left" />
        <NavigationControl position="bottom-left" showCompass={false} />
        <ModeSateliteControl position="bottom-left" />

        <MapMarkerDragHintContainer>
          <Typography variant="caption" mx={1}>
            Dibuja un polígono para delimitar la zona
          </Typography>
        </MapMarkerDragHintContainer>

        <DrawPolygonControl
          ref={drawPolygonControlRef}
          position="top-left"
          onCreate={(event) => handleChangePolygon(event.features)}
          onUpdate={(event) => handleChangePolygon(event.features)}
          onDelete={() => handleChangePolygon(null)}
          color={colorDraw}
          defaultValue={defaultPolygonFeature}
        />

        {warehouses.map((warehouseMarker) => (
          <Marker
            key={`marker-warehouse-${warehouseMarker.warehouseId}`}
            longitude={warehouseMarker.longitude}
            latitude={warehouseMarker.latitude}
            onClick={(event) =>
              handleClickWarehouseMarker(event, warehouseMarker)
            }
            anchor="bottom"
          >
            <MarkerWarehouse color="#00a200" includeBackgroundColor />
          </Marker>
        ))}

        {dataPopupMarker && (
          <Popup
            {...dataPopupMarker.coordinates}
            onClose={handleClosePopupMarker}
            offset={{
              top: [0, 0],
              bottom: [0, -30],
              left: [15, -18],
              right: [-15, -18],
            }}
          >
            <StopsPopupMap {...dataPopupMarker.data} />
          </Popup>
        )}
      </Map>
    </ConfigLogisticZoneFormMapContainer>
  );
};

export default ConfigLogisticZoneFormMap;
